import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import logo from "../../assets/right-click1.png"; // Logo amélioré
import logoDefault from "../../assets/right-click1.png"; // Logo par défaut
import { useAuth } from "../../contexts/SupabaseAuthContext";
import LanguageSwitcher from "../LanguageSwitcher";

// Updated NavItem to support smooth scrolling for anchor links
const NavItem = ({ to, children, ...props }) => {
  const handleClick = (event) => {
    if (to.startsWith("#")) {
      event.preventDefault();
      const target = document.getElementById(to.substring(1));
      if (target) { 
        target.scrollIntoView({ behavior: "smooth" });
      }
    }
    if (props.onClick) props.onClick();
  };

  return to.startsWith("#") ? (
    <a href={to} onClick={handleClick} className="hover:text-secondary transition-colors font-medium" {...props}>
      {children}
    </a>
  ) : (
    <Link to={to} onClick={handleClick} className="hover:text-secondary transition-colors font-medium" {...props}>
      {children}
    </Link>
  );
};

const Navbar = () => {
  const { t } = useTranslation();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [logoSrc, setLogoSrc] = useState(logoDefault);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const projectSuspended = true; // Added project suspension status flag

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isHamburger = windowWidth <= 1280;
  useEffect(() => {
    if (!isHamburger && isMenuOpen) {
      setIsMenuOpen(false);
    }
  }, [isHamburger, isMenuOpen]);

  // Updated navigation links with anchor targets and translations
  const navLinks = [
    { name: t('nav.home'), href: "#home" },
    { name: t('nav.about'), href: "#expertise" },
    { name: t('nav.pricing'), href: "#tarifs" },
    { name: "FAQ", href: "#faq" },
  ];

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await signOut();
      // Clear all storage
      localStorage.clear();
      sessionStorage.clear();
      
      // Small delay to ensure auth state is cleared
      setTimeout(() => {
        // Use replace to prevent going back
        window.location.replace('/');
      }, 100);
    } catch (error) {
      console.error("Logout error:", error);
      setIsLoggingOut(false);
      // If error, try direct navigation
      window.location.replace('/');
    }
  };

  const handleDashboardClick = (e) => {
    if (projectSuspended && !user) {
      e.preventDefault();
      return;
    }
    setIsLoading(true);
    navigate('/dashboard');
    setIsLoading(false);
  };

  const scrollToTarifs = (e) => {
    e.preventDefault();
    const tarifsSection = document.getElementById('tarifs');
    if (tarifsSection) {
      tarifsSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
    setIsMenuOpen(false);
  };

  return (
    <>
      {/* Preloader overlay */}
      {isLoggingOut && (
        <div className="fixed inset-0 bg-white z-[10000] flex flex-col items-center justify-center">
          <svg className="animate-spin h-8 w-8 text-primary mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
          </svg>
          <span>{t('common.logout')}</span>
        </div>
      )}

      {isLoading && (
        <div className="fixed inset-0 bg-white z-[10000] flex flex-col items-center justify-center">
          <svg className="animate-spin h-8 w-8 text-primary mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
          </svg>
          <span>{t('common.loading')}</span>
        </div>
      )}

      <nav className="w-full bg-primary text-white shadow-md fixed top-0 z-1000">
        <div className="container mx-auto px-4 py-1 flex items-center justify-between">
          {/* Logo et slogan */}
          <div className="flex-shrink-0">
            <Link to="/" className="text-2xl font-bold flex items-center">
              <img
                src={logoSrc}
                alt="clicdroit.info Logo"
                className="h-8 w-8 mr-2"
                onMouseEnter={() => setLogoSrc(logo)}
                onMouseLeave={() => setLogoSrc(logoDefault)}
              />
              clicdroits<span className="text-accent">.info</span>
            </Link>
            <span className="text-xs italic opacity-80 block">
              Pour un Québec bien informé
            </span>
          </div>

          {/* Central navigation */}
          <div className={`hidden xl:flex items-center justify-center ${user ? 'ml-8' : ''}`}>
            {navLinks.map((link) => (
              <NavItem 
                key={link.name} 
                to={link.href}
                className="mx-4 py-2 font-medium hover:text-secondary transition-colors"
              >
                {link.name}
              </NavItem>
            ))}
          </div>

          {/* Right side: Language switcher and action buttons */}
          <div className="flex items-center space-x-3">
            {/* Language switcher */}
            <div className="mr-2 z-50">
              <LanguageSwitcher />
            </div>

            {/* Mobile menu button */}
            {isHamburger && (
              <button
                onClick={() => setIsMenuOpen((prev) => !prev)}
                className="focus:outline-none ml-1"
                aria-label="Menu de navigation"
                aria-controls="mobile-menu"
                aria-expanded={isMenuOpen}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d={
                      isMenuOpen
                        ? "M6 18L18 6M6 6l12 12"
                        : "M4 6h16M4 12h16M4 18h16"
                    }
                  />
                </svg>
              </button>
            )}

            {/* Action buttons for desktop */}
            {!isHamburger && (
              <div className="flex items-center space-x-2">
                {user ? (
                  <>
                    <NavItem
                      to="/dashboard"
                      className="bg-white text-primary rounded-xl font-semibold px-4 py-2 text-center shadow-md hover:bg-secondary hover:text-white transition-colors"
                      onClick={handleDashboardClick}
                    >
                      myPlum+
                    </NavItem>
                    <button
                      onClick={handleLogout}
                      className="border px-4 py-2 rounded-full hover:bg-accent hover:text-white transition-colors shadow-lg"
                    >
                      {t('common.logout_btn')}
                    </button>
                  </>
                ) : (
                  <>
                    <NavItem
                      to={projectSuspended ? "#" : "/dashboard"}
                      className={`inline-flex items-center border-none px-4 py-3 font-semibold text-md rounded-2xl shadow-2xl transition-transform duration-100 ${
                        projectSuspended 
                          ? "bg-gray-500 text-white cursor-not-allowed opacity-70" 
                          : "btn btn-warning text-white hover:scale-102 active:scale-95"
                      }`}
                      onClick={(e) => projectSuspended && e.preventDefault()}
                      aria-disabled={projectSuspended}
                    >
                      {projectSuspended ? "Accès suspendu" : "Accéder gratuitement"}
                    </NavItem>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Menu mobile */}
        {isHamburger && isMenuOpen && (
          <div id="mobile-menu" className="bg-primary text-white px-4 pb-4 pt-2 animate-fadeIn">
            <div className="flex flex-col items-center">
              {navLinks.map((link) => (
                <NavItem
                  key={link.name}
                  to={link.href}
                  className="block py-2 hover:text-secondary transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.name}
                </NavItem>
              ))}
            </div>
            <div className="flex flex-col space-y-2 mt-4 items-center">
              {user ? (
                <>
                  <NavItem
                    to="/dashboard"
                    className="bg-white text-primary rounded-xl font-semibold px-4 py-2 text-center shadow-md hover:bg-secondary hover:text-white transition-colors"
                    onClick={handleDashboardClick}
                  >
                    myPlum+
                  </NavItem>
                  <button
                    onClick={handleLogout}
                    className="border px-4 py-2 rounded-full hover:bg-accent hover:text-white transition-colors shadow-lg"
                  >
                    {t('common.logout_btn')}
                  </button>
                </>
              ) : (
                <>
                  <NavItem
                    to={projectSuspended ? "#" : "/dashboard"}
                    className={`inline-flex items-center border-none px-4 py-3 font-semibold text-md rounded-2xl shadow-2xl transition-transform duration-100 ${
                      projectSuspended 
                        ? "bg-gray-500 text-white cursor-not-allowed opacity-70" 
                        : "btn btn-warning text-white hover:scale-102 active:scale-95"
                    }`}
                    onClick={(e) => projectSuspended && e.preventDefault()}
                    aria-disabled={projectSuspended}
                  >
                    {projectSuspended ? "Accès suspendu" : "Accéder gratuitement"}
                  </NavItem>
                </>
              )}
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;
